import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label, LABEL_TYPE } from '@/components/generic';
import { getAssetTypeFromUrl } from '@/helpers';
import { TestIds } from '@/shared/test-ids';

type WaitingScreenProps = {
  landingAssetUrl?: string;
  handleImageClick: () => void;
  description: string;
  className?: string;
};

export const WaitingScreen = ({
  landingAssetUrl,
  handleImageClick,
  description,
  className
}: WaitingScreenProps) => {
  const [t] = useTranslation('common');
  const [isVerticalImage, setIsVerticalImage] = useState(false);
  const [assetType, setAssetType] = useState<'image' | 'video'>('image');

  useEffect(() => {
    if (!landingAssetUrl) {
      return;
    }

    const type = getAssetTypeFromUrl(landingAssetUrl);
    setAssetType(type);

    if (type === 'image') {
      const img = new Image();
      img.src = landingAssetUrl;

      img.onload = () => {
        const isVertical = img.height > img.width;
        setIsVerticalImage(isVertical);
      };
    }
  }, [landingAssetUrl]);

  return (
    <div
      data-testid={TestIds.liveStream.waitingScreenPreview}
      className='relative my-6 flex justify-center rounded-2xl border border-solid border-secondary-pale-blue-gray'>
      {landingAssetUrl ? (
        <>
          {assetType === 'image' ? (
            <img
              data-testid={TestIds.liveStream.waitingScreenPreviewImage}
              src={landingAssetUrl}
              className={`aspect-video w-full cursor-pointer rounded-2xl ${
                isVerticalImage ? 'object-contain' : 'object-cover'
              } ${className}`}
              onClick={handleImageClick}
            />
          ) : (
            <video
              data-testid={TestIds.liveStream.waitingScreenPreviewVideo}
              src={landingAssetUrl}
              className={`${className} aspect-video w-full cursor-pointer rounded-2xl object-contain`}
              onClick={handleImageClick}
              autoPlay
              loop
              muted
              playsInline
            />
          )}
          <div
            data-testid={TestIds.liveStream.waitingScreenDescriptionOverlay}
            className='absolute inset-0 flex h-10 rounded-t-2xl bg-primary-gray'>
            <Label text={description} type={LABEL_TYPE.PARAGRAPH2} className='ml-4 !text-[11px]' />
          </div>
        </>
      ) : (
        <>
          <div
            data-testid={TestIds.liveStream.waitingScreenPreviewPlaceholder}
            className={`flex aspect-video w-full cursor-pointer items-center justify-center rounded-2xl bg-secondary-steel-blue text-white ${className}`}
            onClick={handleImageClick}>
            <div className='w-full px-4 text-center md:px-28 3xl:px-44 4xl:px-44'>
              <Label text={t('waitingScreenNotSelected')} type={LABEL_TYPE.PARAGRAPH2} />
            </div>
          </div>
          <div
            data-testid={TestIds.liveStream.waitingScreenDescriptionOverlay}
            className='absolute inset-0 flex h-10 rounded-t-2xl bg-white bg-opacity-50'>
            <Label text={description} type={LABEL_TYPE.PARAGRAPH2} className='ml-4 !text-[11px]' />
          </div>
        </>
      )}
    </div>
  );
};
